// src/utils/commentGrouping.js
//
// Pure grouping logic behind the Collab tab's comment index: takes the flat
// comment list from the API plus the editor's outline tree, and produces the
// chapter → line structure the panel renders.
//
// Kept out of the component so it can be reasoned about (and tested) without a
// DOM: everything here is a plain data transform.

// Comments whose anchor block no longer exists — or that predate anchoring
// entirely — still need somewhere to live, rather than vanishing from the index.
export const UNANCHORED_KEY = '__unanchored__';

/**
 * Flatten the outline tree into document order.
 *
 * buildOutline() returns a nested tree; grouping only cares about sequence, and
 * a pre-order walk is exactly document order.
 *
 * @param {Array} tree outline tree from buildOutline()
 * @returns {Array<{blockId,text,depth,chapterNo,chapterKey}>} `chapterKey` is
 *          the block-id of the top-level heading each heading sits under (its
 *          own id for a top-level heading)
 */
export function flattenOutline(tree) {
  const out = [];
  let topLevelCount = 0;
  const walk = (nodes, depth, parentChapter) => {
    (nodes || []).forEach((n) => {
      // Only depth-0 headings get a "Ch. N" ordinal — a sub-heading isn't a
      // chapter, so numbering it would misrepresent the document's structure.
      const chapterNo = depth === 0 ? (topLevelCount += 1) : null;
      const chapterKey = depth === 0 ? n.blockId : parentChapter;
      out.push({ blockId: n.blockId, text: n.text, depth, chapterNo, chapterKey });
      if (n.children && n.children.length) walk(n.children, depth + 1, chapterKey);
    });
  };
  walk(tree, 0, null);
  return out;
}

/**
 * Group comments into chapters → lines, in document order.
 *
 * A "chapter" is a top-level heading — the same unit the Chapters panel numbers.
 * Comments under a sub-section roll up into the chapter that section belongs
 * to; giving each section its own group split one chapter into several and
 * made the index read as a list of lines rather than chapters.
 *
 * Read state rolls up: a line is "read" once every comment on it has been read
 * by this viewer, and a chapter is "read" once every line under it is. That's
 * what drives the dimming in the design.
 *
 * @param {Array} comments  from the comments API (carry block_id / read)
 * @param {Array} tree      outline tree from buildOutline()
 * @param {(c:object)=>number|null} lineNumberFor resolves a display line number
 * @param {(c:object)=>string|null} chapterFor resolves the owning chapter's
 *        block-id from the CURRENT document; falls back to the comment's
 *        stored chapter_id when omitted
 * @returns {Array<{key,title,chapterNo,depth,allRead,lines:Array}>}
 */
export function groupComments(comments, tree, lineNumberFor, chapterFor) {
  const headings = flattenOutline(tree).filter((h) => h.blockId);
  // Any heading → the chapter it sits in.
  const chapterOf = new Map(headings.map((h) => [h.blockId, h.chapterKey]));

  // chapterKey -> blockKey -> comment[]
  const buckets = new Map();
  (comments || []).forEach((c) => {
    // Prefer the chapter the block sits in RIGHT NOW; a stored chapter_id goes
    // stale the moment a paragraph is moved between chapters. Either way, a
    // chapter that no longer exists falls back to the unanchored bucket, so
    // deleting a heading never hides its feedback.
    const resolved = chapterFor ? chapterFor(c) : c.chapter_id;
    const chapterKey = chapterOf.get(resolved) || UNANCHORED_KEY;
    const blockKey = c.block_id || `${chapterKey}:no-block`;
    if (!buckets.has(chapterKey)) buckets.set(chapterKey, new Map());
    const lines = buckets.get(chapterKey);
    if (!lines.has(blockKey)) lines.set(blockKey, []);
    lines.get(blockKey).push(c);
  });

  const groups = [];
  const emit = (key, meta) => {
    const lines = buckets.get(key);
    if (!lines || lines.size === 0) return;

    const lineRows = Array.from(lines.entries()).map(([blockKey, list]) => {
      // Every comment on a block shares its line, so the first resolvable
      // number wins.
      let lineNo = null;
      for (let i = 0; i < list.length; i += 1) {
        const n = lineNumberFor ? lineNumberFor(list[i]) : null;
        if (n != null) { lineNo = n; break; }
      }
      return {
        key: blockKey,
        blockId: list[0].block_id || null,
        lineNo,
        comments: list,
        allRead: list.every((c) => !!c.read),
        // Unnumbered lines sort last but keep a stable relative order.
        sortKey: lineNo != null ? lineNo : Number.MAX_SAFE_INTEGER,
      };
    });

    lineRows.sort((a, b) => a.sortKey - b.sortKey);

    groups.push({
      key,
      ...meta,
      lines: lineRows,
      allRead: lineRows.every((l) => l.allRead),
    });
  };

  headings.filter((h) => h.depth === 0).forEach((ch) => emit(ch.blockId, {
    title: ch.text,
    chapterNo: ch.chapterNo,
    depth: ch.depth,
  }));
  // Unanchored last — it's a fallback bucket, not part of the narrative order.
  emit(UNANCHORED_KEY, { title: 'Unassigned', chapterNo: null, depth: 0 });

  return groups;
}
